import * as React from 'react';
import { Button, Col, Dropdown, Menu, Radio, RadioChangeEvent, Row, Tooltip } from 'antd';
import { BookOutlined, FileDoneOutlined } from '@ant-design/icons';
import styled from 'styled-components/macro';
import { Link } from 'react-router-dom';

import { AutoTooltip } from '../../entity/shared/components/common/AutoTooltip';
import { postMessageAction, postMessageService } from '../postMessageService';
import { getHelpCenterUrlFromPathname } from '../utils/helpCenterUrlUtils';
import { usePageType } from '../../search/context/BreadcrumbContext';
import { useUserContext } from '../../context/useUserContext';
import { useTranslation } from '../../../i18n/useTranslation';
import { useRbac } from '../../entity/shared/useRbac';
import { pageTypeMap } from '../pageTypeConstants';

import { ReactComponent as IconAnalytics } from '../../../images/icon-data-analytics.svg';
import { ReactComponent as IconArrowDown } from '../../../images/keyboard_arrow_down.svg';
import { ReactComponent as IconHelpLink } from '../../../images/icon-help-outline.svg';
import { ReactComponent as IconDataGovern } from '../../../images/icon-govern.svg';
import { ReactComponent as HamburgerIcon } from '../../../images/icon-more-vert.svg';
import { ReactComponent as IconBoardReporting } from '../../../images/icon-board-reporting.svg';
import DomainIcon from '../../domain/DomainIcon';

export enum IngestionTabType {
    Sources = 'Sources',
    Secrets = 'Secrets',
}

const LinksWrapper = styled(Row)<{ areLinksHidden?: boolean }>`
    opacity: 1;
    transition: opacity 0.5s;

    display: flex;
    justify-content: flex-end;
    padding-right: 32px;

    ${(props) =>
        props.areLinksHidden &&
        `
        opacity: 0;
        width: 0;
    `}
`;

const MenuItem = styled(Menu.Item)`
    font-size: 14px;
    font-weight: bold;
    max-width: 240px;
`;

const NavTitleContainer = styled.span`
    gap: 8px;
    display: flex;
    align-items: center;
`;

const NavLink = styled(Link)`
    &&:after {
        content: none;
    }
`;

interface Props {
    isClassicView?: boolean;
    areLinksHidden?: boolean;
    ingestionTabType?: IngestionTabType;
    showAnalyticsButton?: boolean;
    showExportPdf?: boolean;
    /**
     * Export state/handler are owned by the caller (HomePage) so it can show its loading
     * indicator (ComponentSpinner above the breadcrumb) instead of this shared component
     * reaching into page-specific layout.
     */
    isExportingPdf?: boolean;
    onExportPdf?: () => void;
    handleIngestionModeChange?: (e: RadioChangeEvent) => void;
}

export function HeaderLinks(props: Props) {
    const {
        areLinksHidden,
        showAnalyticsButton,
        showExportPdf,
        isExportingPdf,
        onExportPdf,
        isClassicView = false,
    } = props;

    const me = useUserContext();
    const { t } = useTranslation();
    const pageType = usePageType();
    const { canManageGlossary, resolveIngestionPrivileges, resolveSecretPrivileges } = useRbac();

    const showAnalytics = (showAnalyticsButton && me && me?.platformPrivileges?.viewAnalytics) || false;

    const showDomains = me?.platformPrivileges?.viewDomain || me?.platformPrivileges?.manageDomains || false;
    const showDataProducts =
        me?.platformPrivileges?.viewDataProduct || me?.platformPrivileges?.manageDataProducts || false;

    const showGlossary = canManageGlossary();

    const showGovernance = showGlossary || showDomains || showDataProducts;

    const { canView: showSecrets } = resolveSecretPrivileges();
    const { canView: showSources } = resolveIngestionPrivileges();

    const handleHelpCenterClick = () => {
        const url = new URL(window.location.href);
        const { pathname } = url;
        const helpCenterUrl = getHelpCenterUrlFromPathname(pathname);
        postMessageService.sendToParent({
            action: postMessageAction.OPEN_HELP_CENTER,
            url: helpCenterUrl,
            pageType,
        });
    };

    return (
        <LinksWrapper className="f-gap-2" wrap={false} areLinksHidden={areLinksHidden}>
            {pageType === pageTypeMap.INGESTION && (
                <Col xs={24} lg={12} className="text-center">
                    <Radio.Group
                        className="f-btn-tabs mx-auto"
                        value={props.ingestionTabType}
                        onChange={props.handleIngestionModeChange}
                    >
                        <Tooltip
                            title={!showSources ? t('UNAUTHORISED_TOOLTIP') : undefined}
                            overlayClassName="f-tooltip-white"
                        >
                            <Radio.Button disabled={!showSources} value={IngestionTabType.Sources}>
                                {t('Sources')}
                            </Radio.Button>
                        </Tooltip>
                        <Tooltip
                            title={!showSecrets ? t('UNAUTHORISED_TOOLTIP') : undefined}
                            overlayClassName="f-tooltip-white"
                        >
                            <Radio.Button disabled={!showSecrets} value={IngestionTabType.Secrets}>
                                {t('Secrets')}
                            </Radio.Button>
                        </Tooltip>
                    </Radio.Group>
                </Col>
            )}
            {pageType !== pageTypeMap.INGESTION && isClassicView && (
                <>
                    {showAnalytics && (
                        <Col flex="none">
                            <Tooltip title={t('View data catalog usage analytics')}>
                                <Button
                                    type="text"
                                    aria-label={t('Analytics')}
                                    onClick={() => {
                                        window.location.href = '/analytics';
                                    }}
                                >
                                    <IconAnalytics aria-hidden="true" />
                                    <span>{t('Analytics')}</span>
                                </Button>
                            </Tooltip>
                        </Col>
                    )}
                    <Col flex="none">
                        {showGovernance && (
                            <Dropdown
                                trigger={['click']}
                                overlay={
                                    <Menu className="f-dropdown-medium">
                                        {showGlossary && (
                                            <MenuItem key="0">
                                                <NavLink to="/glossary">
                                                    <NavTitleContainer aria-label={t('Glossary')}>
                                                        <BookOutlined />
                                                        <span>{t('Glossary')}</span>
                                                    </NavTitleContainer>
                                                    <AutoTooltip className="f-text-content f-color-dark-black-s70">
                                                        {t('VIEW_MODIFY_DATA_DICTIONARY_DESC')}
                                                    </AutoTooltip>
                                                </NavLink>
                                            </MenuItem>
                                        )}

                                        {showDomains && (
                                            <MenuItem key="1">
                                                <NavLink to="/domains">
                                                    <NavTitleContainer aria-label={t('Domains')}>
                                                        <DomainIcon ariaLabel={t('Domains')} />
                                                        <span>{t('Domains')}</span>
                                                    </NavTitleContainer>
                                                    <AutoTooltip className="f-text-content f-color-dark-black-s70">
                                                        {t('MANAGE_RELATED_DATA_ASSETS_DESC')}
                                                    </AutoTooltip>
                                                </NavLink>
                                            </MenuItem>
                                        )}
                                        {showDataProducts && (
                                            <MenuItem key="2">
                                                <NavLink to="/browse/dataProduct">
                                                    <NavTitleContainer aria-label={t('Data Products')}>
                                                        <FileDoneOutlined />
                                                        <span>{t('Data Products')}</span>
                                                    </NavTitleContainer>
                                                    <AutoTooltip className="f-text-content f-color-dark-black-s70">
                                                        {t('ORGANIZE_MANAGE_DATA_ASSETS_DESC')}
                                                    </AutoTooltip>
                                                </NavLink>
                                            </MenuItem>
                                        )}
                                    </Menu>
                                }
                            >
                                <Button type="text" aria-label={t('Govern')}>
                                    <IconDataGovern aria-hidden="true" /> {t('Govern')}{' '}
                                    <IconArrowDown aria-hidden="true" />
                                </Button>
                            </Dropdown>
                        )}
                    </Col>
                </>
            )}
            {showExportPdf && (
                <Col flex="none">
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        overlay={
                            <Menu className="f-dropdown-small">
                                <Menu.Item
                                    onClick={onExportPdf}
                                    disabled={isExportingPdf}
                                    icon={<IconBoardReporting />}
                                >
                                    {t('Generate Report')}
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button type="text" className="f-btn-action">
                            <HamburgerIcon />
                        </Button>
                    </Dropdown>
                </Col>
            )}
            <Col flex={pageType === pageTypeMap.INGESTION ? 'auto' : 'none'} className="text-right">
                <Tooltip title={t('VIEW_CATALOG_HELP_CENTER_DESC')} placement="bottomRight">
                    <Button
                        type="text"
                        className="f-btn-action"
                        aria-label={t('Help Center')}
                        onClick={handleHelpCenterClick}
                        icon={<IconHelpLink aria-hidden="true" />}
                    />
                </Tooltip>
            </Col>
        </LinksWrapper>
    );
}
